import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";

import toast from "react-hot-toast";

import {
  Check,
  Trash2,
  ExternalLink,
  AlertCircle,
  CreditCard,
  FileCheck,
  PieChart,
  Calendar,
  Shield,
  Bell,
  Wallet,
  TrendingUp,
  X,
} from "lucide-react";
import { useNotification } from "../../../contexts/notification/NotificationContext";
import "react-toastify/dist/ReactToastify.css";

// Your existing constants...
const NOTIFICATION_ICONS = {
  system: AlertCircle,
  transaction: CreditCard,
  kyc: FileCheck,
  portfolio: PieChart,
  appointment: Calendar,
  admin: Shield,
  general: Bell,
  wallet: Wallet,
  investment: TrendingUp,
};

const PRIORITY_CONFIGS = {
  low: {
    textColor: "text-blue-600",
    bgColor: "bg-blue-100",
    badgeClasses: "bg-blue-50 text-blue-700 border border-blue-200",
  },
  medium: {
    textColor: "text-green-600",
    bgColor: "bg-green-100",
    badgeClasses: "bg-green-50 text-green-700 border border-green-200",
  },
  high: {
    textColor: "text-amber-600",
    bgColor: "bg-amber-100",
    badgeClasses: "bg-amber-50 text-amber-700 border border-amber-200",
  },
  urgent: {
    textColor: "text-red-600",
    bgColor: "bg-red-100",
    badgeClasses: "bg-red-50 text-red-700 border border-red-200",
  },
};

// Delete Confirmation Modal Component
const DeleteConfirmationModal = ({ isOpen, onClose, onConfirm, title }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Delete Notification
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
              <AlertCircle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-gray-700">
                Are you sure you want to delete this notification?
              </p>
              <p className="text-xs text-gray-500 mt-1 font-medium">
                "{title}"
              </p>
            </div>
          </div>
          <p className="text-xs text-gray-500">This action cannot be undone.</p>
        </div>

        <div className="flex gap-3 p-6 pt-0">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

const ActionButton = ({ icon, onClick, title, hoverColor }) => {
  const hoverClasses = {
    indigo: "hover:bg-indigo-100 hover:text-indigo-600",
    red: "hover:bg-red-100 hover:text-red-600",
  };

  return (
    <button
      onClick={onClick}
      title={title}
      className={`p-2 text-gray-400 transition-colors duration-200 rounded-md ${
        hoverClasses[hoverColor] || hoverClasses.indigo
      }`}
    >
      {icon}
    </button>
  );
};

const NotificationItem = ({ notification, isInDropdown = false }) => {
  const { markAsRead, deleteNotification } = useNotification();
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const {
    _id,
    isRead = false,
    type = "general",
    priority = "medium",
    title = "",
    message = "",
    createdAt = new Date(),
    actionUrl = "",
  } = notification || {};

  const IconComponent = useMemo(() => NOTIFICATION_ICONS[type] || Bell, [type]);
  const priorityConfig = useMemo(
    () => PRIORITY_CONFIGS[priority] || PRIORITY_CONFIGS.medium,
    [priority]
  );
  const formattedTime = useMemo(
    () => formatDistanceToNow(new Date(createdAt), { addSuffix: true }),
    [createdAt]
  );

  const handleClick = useCallback(() => {
    if (!isRead) {
      markAsRead([_id]);
    }

    switch (type) {
      case "portfolio":
        navigate("/portfolio");
        break;
      case "transaction":
        navigate("/wallet");
        break;
      case "kyc":
        navigate("/profile/kyc");
        break;
      case "appointment":
        navigate("/myappointments");
        break;
      case "investment":
        navigate("/mutual-funds");
        break;
      case "wallet":
        navigate("/wallet");
        break;
      default:
        if (actionUrl) {
          navigate(actionUrl);
        } else {
          navigate("/portfolio");
        }
    }
  }, [_id, isRead, markAsRead, navigate, type, actionUrl]);

  const handleMarkAsRead = useCallback(
    (e) => {
      e.stopPropagation();
      markAsRead([_id]);
    },
    [_id, markAsRead]
  );

  // Open delete confirmation modal
  const handleDeleteClick = useCallback((e) => {
    e.stopPropagation();
    setShowDeleteModal(true);
  }, []);

  // Close delete confirmation modal
  const handleCloseModal = useCallback(() => {
    setShowDeleteModal(false);
  }, []);

  // Confirm delete action
  const handleConfirmDelete = useCallback(() => {
    try {
      deleteNotification(_id);
      toast.success("Deleted Successfully!", {
        position: "bottom-right", // Add this option
      });
      setShowDeleteModal(false);
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete notification", {
        position: "bottom-right", // Add this option
      });
    }
  }, [_id, deleteNotification]);

  const containerClasses = useMemo(
    () => `
    flex items-start p-5 cursor-pointer gap-4 transition-all duration-200
    ${isRead ? "bg-white" : "bg-indigo-50/50"}
    ${
      isInDropdown
        ? "hover:bg-gray-50"
        : "hover:bg-gray-50 border-b border-gray-100 relative"
    }
    ${
      !isRead && !isInDropdown
        ? "before:absolute before:left-0 before:top-0 before:h-full before:w-1 before:bg-indigo-500"
        : ""
    }
  `,
    [isRead, isInDropdown]
  );

  return (
    <>
      <div className={containerClasses} onClick={handleClick}>
        <div
          className={`flex-shrink-0 ${priorityConfig.bgColor} rounded-full p-2.5`}
        >
          <IconComponent size={20} className={priorityConfig.textColor} />
        </div>

        <div className="flex-grow min-w-0">
          <div className="flex justify-between items-start mb-1.5">
            <h4 className="font-semibold text-gray-900 truncate pr-2 text-base">
              {title}
            </h4>
            <span className="text-xs text-gray-500 whitespace-nowrap font-medium">
              {formattedTime}
            </span>
          </div>

          <p className="text-sm text-gray-700 leading-relaxed mb-3">
            {message}
          </p>

          {!isInDropdown && (
            <div className="flex gap-2 text-xs mt-1">
              <span className="px-2.5 py-1 rounded-full bg-gray-100 text-gray-700 capitalize font-medium border border-gray-200">
                {type}
              </span>
              <span
                className={`px-2.5 py-1 rounded-full capitalize font-medium ${priorityConfig.badgeClasses}`}
              >
                {priority}
              </span>
            </div>
          )}
        </div>

        <div className="flex flex-shrink-0 flex-col gap-2.5">
          {!isRead && (
            <ActionButton
              icon={<Check size={16} />}
              onClick={handleMarkAsRead}
              title="Mark as read"
              hoverColor="indigo"
            />
          )}

          {!isInDropdown && (
            <ActionButton
              icon={<Trash2 size={16} />}
              onClick={handleDeleteClick}
              title="Delete notification"
              hoverColor="red"
            />
          )}

          {actionUrl && !isInDropdown && (
            <ActionButton
              icon={<ExternalLink size={16} />}
              onClick={handleClick}
              title="Open related item"
              hoverColor="indigo"
            />
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={handleCloseModal}
        onConfirm={handleConfirmDelete}
        title={title}
      />
    </>
  );
};

export default NotificationItem;
